import { useEffect, useState } from "react";
import "../styles/InventoryMaster.css";
import { useOutletContext } from "react-router-dom";
import { Package2 } from "lucide-react";
import * as services from "../api/services";

const InventoryMaster = () => {

  const [units, setUnits] = useState(["PCS", "KGS", "BTL"]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [godowns, setGodowns] = useState(["Main Warehouse"]);

  const [unitForm, setUnitForm] = useState({ symbol: "", code: "", decimal: "" });
  const { collapsed } = useOutletContext();
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });
  const [godownForm, setGodownForm] = useState({ name: "", description: "", address: "" });

  const [itemForm, setItemForm] = useState({
    name: "", description: "", unit: "", category: "", godown: "",
    gstApplicable: true, hsn: "", gstDescription: "", taxability: "Taxable",
    gstRate: "", supplyType: "Goods", qty: "", rate: "", amount: ""
  });

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await services.getItemCategories();
      const data = res.data.results || res.data || [];
      setCategories(data);
    } catch {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchGodowns = async () => {
    try {
      const res = await services.getGodowns();
      const data = res.data.results || res.data || [];
      setGodowns(data.map((g) => g.name));
    } catch {
      setGodowns([]);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchGodowns();
  }, []);

  useEffect(() => {
    const qty = Number(itemForm.qty || 0);
    const rate = Number(itemForm.rate || 0);
    setItemForm((prev) => ({ ...prev, amount: qty * rate }));
  }, [itemForm.qty, itemForm.rate]);

  const saveUnit = () => {
    if (!unitForm.symbol) return;
    setUnits((prev) => [...prev, unitForm.symbol]);
    setUnitForm({ symbol: "", code: "", decimal: "" });
  };

  const saveCategory = async () => {
    if (!categoryForm.name) return;
    try {
      await services.createItemCategory({
        name: categoryForm.name,
        description: categoryForm.description
      });
      setCategoryForm({ name: "", description: "" });
      fetchCategories();
    } catch {
      // keep form as is on error
    }
  };

  const saveGodown = async () => {
    if (!godownForm.name) return;
    try {
      await services.createGodown({
        name: godownForm.name,
        description: godownForm.description,
        address: godownForm.address
      });
      setGodownForm({ name: "", description: "", address: "" });
      fetchGodowns();
    } catch {
      // keep form as is on error
    }
  };

  const saveItem = async () => {
    try {
      const category = categories.find((c) => c.name === itemForm.category);
      const unit = ["NONE", "BOX", "BAG", "BOTTLE", "BUNDLES"].includes(
        (itemForm.unit || "").toUpperCase()
      )
        ? (itemForm.unit || "").toUpperCase()
        : "NONE";
      const gstTypeMap = {
        "Taxable": "GST",
        "Non-GST": "EXEMPT",
        "Nil GST": "EXEMPT",
        "Exempt": "EXEMPT",
      };
      await services.createItem({
        name: itemForm.name,
        primary_unit: unit,
        category: category ? category.id : null,
        location: itemForm.godown || "",
        hsn_code: itemForm.hsn || "",
        gst_value: itemForm.gstRate ? Number(itemForm.gstRate) : null,
        gst_type: itemForm.gstApplicable
          ? gstTypeMap[itemForm.taxability] || "GST"
          : "EXEMPT",
        opening_quantity: Number(itemForm.qty) || 0,
        at_price: itemForm.rate ? Number(itemForm.rate) : null,
      });
      setItemForm({
        name: "", description: "", unit: "", category: "", godown: "",
        gstApplicable: true, hsn: "", gstDescription: "", taxability: "Taxable",
        gstRate: "", supplyType: "Goods", qty: "", rate: "", amount: ""
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className={`main ${collapsed ? "collapsed" : ""}`}>
        <div className="inventory-page">
            <div className="inventory-header">
                <div className="inventory-header-left">
                    <div className="inventory-header-icon">
                    <Package2 size={26} />
                    </div>
                    <div>
                    <h1>Item Group</h1>
                    <p>
                        Manage stock items, categories,
                        units and godowns
                    </p>
                    </div>
                </div>
                <div className="inventory-header-right">
                    <div className="inventory-badge">
                     Inventory Module
                    </div>
                </div>
            </div>
        <div className="inventory-top-grid">
            {/* UNIT */}
            <div className="inventory-card">
                <div className="inventory-card-title">
                    Stock Unit
                </div>
                <div className="inventory-form-group">
                    <label>Symbol</label>
                    <input
                    value={unitForm.symbol}
                    onChange={(e) =>
                        setUnitForm({ ...unitForm, symbol: e.target.value })
                    }
                    placeholder="PCS"
                    />
                </div>
                <div className="inventory-form-group">
                    <label>Unit Code</label>
                    <select
                    value={unitForm.code}
                    onChange={(e) =>
                        setUnitForm({ ...unitForm, code: e.target.value })
                    }
                    >
                    <option value="">Select</option>
                    <option>PCS - Pieces</option>
                    <option>KGS - Kilograms</option>
                    <option>BTL - Bottles</option>
                    </select>
                </div>
                <div className="inventory-form-group">
                    <label>Decimal Places</label>
                    <input
                    value={unitForm.decimal}
                    onChange={(e) =>
                        setUnitForm({ ...unitForm, decimal: e.target.value })
                    }
                    placeholder="2"
                    />
                </div>
                <div className="inventory-actions">
                    <button className="unit-inventory-save-btn" onClick={saveUnit}>
                    Save
                    </button>
                </div>
            </div>
            {/* CATEGORY */}
            <div className="inventory-card">
                <div className="inventory-card-title">
                    Stock Category
                </div>
                <div className="inventory-form-group">
                    <label>Name</label>
                    <input
                    value={categoryForm.name}
                    onChange={(e) =>
                        setCategoryForm({ ...categoryForm, name: e.target.value })
                    }
                    placeholder="Electronics"
                    />
                </div>
                <div className="inventory-form-group">
                    <label>Description</label>
                    <textarea
                    rows="3"
                    value={categoryForm.description}
                    onChange={(e) =>
                        setCategoryForm({ ...categoryForm, description: e.target.value })
                    }
                    />
                </div>
                <div className="inventory-actions">
                    <button className="stock-inventory-save-btn" onClick={saveCategory}>
                    Save
                    </button>
                </div>
                {categories.length > 0 && (
                    <div style={{ marginTop: 10, borderTop: "1px solid #eee", paddingTop: 10 }}>
                        <label style={{ fontWeight: 600, fontSize: 12, color: "#666" }}>Existing Categories</label>
                        <ul style={{ margin: "6px 0 0", padding: "0 0 0 16px", fontSize: 13 }}>
                            {categories.map((c) => (
                                <li key={c.id}>{c.name}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {categoriesLoading && <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>Loading...</div>}
            </div>
            {/* GODOWN */}
            <div className="inventory-card">
                <div className="inventory-card-title">
                    Stock Godown
                </div>
                <div className="inventory-form-group">
                    <label>Name</label>
                    <input
                    value={godownForm.name}
                    onChange={(e) =>
                        setGodownForm({ ...godownForm, name: e.target.value })
                    }
                    placeholder="Main Warehouse"
                    />
                </div>
                <div className="inventory-form-group">
                    <label>Description</label>
                    <input
                    value={godownForm.description}
                    onChange={(e) =>
                        setGodownForm({ ...godownForm, description: e.target.value })
                    }
                    />
                </div>
                <div className="inventory-form-group">
                    <label>Address</label>
                    <textarea
                    rows="2"
                    value={godownForm.address}
                    onChange={(e) =>
                        setGodownForm({ ...godownForm, address: e.target.value })
                    }
                    />
                </div>
                <div className="inventory-actions">
                    <button className="inventory-save-btn" onClick={saveGodown}>
                    Save
                    </button>
                </div>
            </div>
        </div>
        <div className="inventory-item-card">
            <div className="inventory-main-title">
            Stock Item
            </div>
            <div className="inventory-item-grid">
            <div className="inventory-item-section">
                <div className="inventory-form-group">
                <label>Name</label>
                <input
                    value={itemForm.name}
                    onChange={(e) =>
                    setItemForm({ ...itemForm, name: e.target.value })
                    }
                />
                </div>
                <div className="inventory-form-group">
                    <label>Unit</label>
                    <select
                        value={itemForm.unit}
                        onChange={(e) =>
                        setItemForm({ ...itemForm, unit: e.target.value })
                        }
                    >
                        <option value="">Select Unit</option>
                        {units.map((u, i) => (
                        <option key={i}>{u}</option>
                        ))}
                    </select>
                </div>
                <div className="inventory-form-group">
                    <label>Description</label>
                    <textarea
                        rows="3"
                        value={itemForm.description}
                        onChange={(e) =>
                        setItemForm({ ...itemForm, description: e.target.value })
                        }
                    />
                </div>
                <div className="inventory-form-group">
                <label>Category</label>
                <select
                    value={itemForm.category}
                    onChange={(e) =>
                    setItemForm({ ...itemForm, category: e.target.value })
                    }
                >
                    <option value="">Select Category</option>
                    {categories.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                </select>
                </div>
                <div className="inventory-form-group">
                <label>Godown</label>
                <select
                    value={itemForm.godown}
                    onChange={(e) =>
                    setItemForm({ ...itemForm, godown: e.target.value })
                    }
                >
                    <option value="">Select Godown</option>
                    {godowns.map((g, i) => (
                    <option key={i}>{g}</option>
                    ))}
                </select>
                </div>
            </div>
            <div className="inventory-item-section">
                <div className="inventory-radio">
                <label>GST Applicable</label>
                <div className="inventory-radio-group">
                    <label>
                    <input
                        type="radio"
                        checked={itemForm.gstApplicable}
                        onChange={() =>
                        setItemForm({ ...itemForm, gstApplicable: true })
                        }
                    />
                    Yes
                    </label>
                    <label>
                    <input
                        type="radio"
                        checked={!itemForm.gstApplicable}
                        onChange={() =>
                        setItemForm({ ...itemForm, gstApplicable: false })
                        }
                    />
                    No
                    </label>
                </div>
                </div>
                {itemForm.gstApplicable && (
                <>
                    <div className="inventory-form-group">
                    <label>HSN / SAC</label>
                    <input
                        value={itemForm.hsn}
                        onChange={(e) =>
                        setItemForm({ ...itemForm, hsn: e.target.value })
                        }
                    />
                    </div>
                    <div className="inventory-form-group">
                    <label>Description Of Item</label>
                    <input
                        value={itemForm.gstDescription}
                        onChange={(e) =>
                        setItemForm({ ...itemForm, gstDescription: e.target.value })
                        }
                    />
                    </div>
                    <div className="inventory-form-group">
                    <label>Taxability</label>
                    <select
                        value={itemForm.taxability}
                        onChange={(e) =>
                        setItemForm({ ...itemForm, taxability: e.target.value })
                        }
                    >
                        <option>Taxable</option>
                        <option>Exempt</option>
                        <option>Nil GST</option>
                        <option>Non-GST</option>
                    </select>
                    </div>
                    <div className="inventory-form-group">
                    <label>GST Rate</label>
                    <input
                        value={itemForm.gstRate}
                        onChange={(e) =>
                        setItemForm({ ...itemForm, gstRate: e.target.value })
                        }
                        placeholder="18%"
                    />
                    </div>
                    <div className="inventory-form-group">
                    <label>Type Of Supply</label>
                    <select
                        value={itemForm.supplyType}
                        onChange={(e) =>
                        setItemForm({ ...itemForm, supplyType: e.target.value })
                        }
                    >
                        <option>Goods</option>
                        <option>Service</option>
                        <option>Capital Goods</option>
                    </select>
                    </div>
                </>
                )}
            </div>
            </div>
            <div className="inventory-opening">
            <div className="inventory-opening-title">
                Opening Balance
            </div>
            <div className="inventory-opening-grid">
                <div className="inventory-form-group">
                <label>Qty</label>
                <input
                    value={itemForm.qty}
                    onChange={(e) =>
                    setItemForm({ ...itemForm, qty: e.target.value })
                    }
                />
                </div>
                <div className="inventory-form-group">
                <label>Rate</label>
                <input
                    value={itemForm.rate}
                    onChange={(e) =>
                    setItemForm({ ...itemForm, rate: e.target.value })
                    }
                />
                </div>
                <div className="inventory-form-group">
                <label>Amount</label>
                <input value={itemForm.amount} readOnly />
                </div>
            </div>
            </div>
            <div className="inventory-actions">
            <button className="inventory-save-btn" onClick={saveItem}>
                Save Item
            </button>
            </div>
        </div>
        </div>
    </div>
  );
};

export default InventoryMaster;
